import { useCallback, type ReactNode } from 'react';
import type { TooltipContentProps } from 'recharts';
import type {
  NameType,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent';
import { useI18nContext } from '@/i18n/i18n-react';
import { partialReadoutLabel } from '@/lib/partial-buckets';

export type ChartTooltipRow = {
  label: string;
  value: string;
  color?: string;
};

export type ChartTooltipFormatter = (
  props: TooltipContentProps<ValueType, NameType>
) => ReactNode;

type UseChartTooltipOptions = {
  formatValue?: (value: number) => string;
  formatRows?: (
    label: string,
    payload: TooltipContentProps<ValueType, NameType>['payload']
  ) => ChartTooltipRow[];
  /** Rendered below the rows, separated by a top border (e.g. a click hint). */
  footer?: ReactNode;
  /**
   * Campaigns of the hovered bucket (row = the chart row), listed under
   * "Campanhas" with their send time (HOME-04 on the Analytics charts).
   */
  campaigns?: (
    row: Record<string, unknown>
  ) => Array<{ name: string; time: string }>;
};

const isRow = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * Returns a Recharts tooltip render function with consistent card styling.
 * The label of a partial bucket (row with `partial: true`) gets "· parcial".
 */
export function useChartTooltip({
  formatValue = (v) => String(v),
  formatRows,
  footer,
  campaigns,
}: UseChartTooltipOptions = {}): ChartTooltipFormatter {
  const { LL } = useI18nContext();
  return useCallback(
    ({ active, payload, label }: TooltipContentProps<ValueType, NameType>) => {
      if (!active || !payload?.length) return null;

      const hovered = payload[0]?.payload;
      const bucketCampaigns =
        campaigns && isRow(hovered) ? campaigns(hovered) : [];
      // CHART-05: a partial bucket (row flagged by `markPartialRows`) reads
      // "{label} · parcial".
      const title = partialReadoutLabel(
        String(label ?? ''),
        isRow(hovered) && hovered.partial === true,
        LL
      );

      const rows = formatRows
        ? formatRows(String(label ?? ''), payload)
        : [
            {
              label: String(payload[0]?.name ?? ''),
              value: formatValue((payload[0]?.value as number) ?? 0),
            },
          ];

      return (
        <div className="rounded-lg border border-input bg-background p-2 shadow-sm">
          <p className="text-sm text-muted-foreground">{title}</p>
          {rows.map((row, index) => (
            <p
              key={`${row.label}-${index}`}
              className="text-sm"
              style={row.color ? { color: row.color } : undefined}
            >
              {row.label ? `${row.label}: ` : null}
              {row.value}
            </p>
          ))}
          {bucketCampaigns.length ? (
            <div className="mt-1.5 border-t border-border pt-1.5">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {LL.chartCampaigns.tooltipTitle()}
              </p>
              {bucketCampaigns.map((campaign, index) => (
                <p
                  key={`${campaign.name}-${index}`}
                  className="flex justify-between gap-3 text-sm"
                >
                  <span>{campaign.name}</span>
                  <span className="text-muted-foreground">{campaign.time}</span>
                </p>
              ))}
            </div>
          ) : null}
          {footer ? (
            <div className="mt-1.5 border-t border-border pt-1.5">{footer}</div>
          ) : null}
        </div>
      );
    },
    [formatValue, formatRows, footer, campaigns, LL]
  );
}
